import { AiOutlinePlus } from "react-icons/ai";
import EditJobTitle from "./EditJobTitle";
import NewJobTitle from "./NewJobTitle";
import SkewLoader from "react-spinners/SkewLoader";
import { GoUpload } from "react-icons/go";
import { useEffect, useRef, useState } from "react";
import { JobTitleDto } from "../../../../../models/job-titles.dto";
import { useGetJobTitle } from "../../../../../services/job-title.service";
import { CompanyLevelOrderBy } from "../../../../../models/company-level.dto";
import { DisabilitiesOrderBy } from "../../../../../models/disabilities.dto";
import { EmployeeBandOrderBy } from "../../../../../models/employee-band.dto";
import { useGetCompanyLevel } from "../../../../../services/company-level.service";
import { useGetDisabilities } from "../../../../../services/disabilities.service";
import { useGetEmployeeBand } from "../../../../../services/employee-band.service";
import useUserStore from "../../../../../state-management/useUserStore";
import useCompanyStore from "../../../../../state-management/useCompanyStore";
import { useManageAccordions } from "../../../../../helpers/useManageAccordions";
import CustomPagination from "../../../../../components/table/CustomPagination";
import { APP_CONFIG } from "../../../../../helpers/appHelpers";
import {
	useCheckUserPermissions,
	PermissionChecker,
	PERMISSIONS,
} from "../../../../../helpers/usePermissionManager";
import UploadJobTitles from "./modal/UploadJobTitles";
import { toast } from "react-toastify";
import { getAccessToken } from "../../../../../services/utils/app.data";
import ENDPOINTS from "../../../../../services/utils/endpoints";
import { ChevronDown, Upload } from "lucide-react";
import { BsDownload } from "react-icons/bs";
import ExportJobTitlesModal from "./modal/ExportJobTitlesModal";

const JobTitles = () => {
	const { currentUser } = useUserStore();
	const { currentCompany } = useCompanyStore();
	const { checkUserPermissionsAnd } = useCheckUserPermissions();
	const [showNewJobTitleForm, setShowNewJobTitleForm] = useState(false);
	const [openUploadJobTitlesModal, setOpenUploadJobTitlesModal] =
		useState(false);
	const [isOpen, setIsOpen] = useState(false);
	const [openExportModal, setOpenExportModal] = useState(false);
	const dropdownRef = useRef<HTMLDivElement | null>(null);

	const {
		accordionsData: jobTitles,
		isLoading,
		pageCount,
	} = useManageAccordions<JobTitleDto>({
		fetchHook: useGetJobTitle,
		options: { companyId: currentCompany?.id },
	});

	const { data: companyLevels, isLoading: loadingCompanyLevels } =
		useGetCompanyLevel({
			companyId: currentCompany?.id,
			limit: APP_CONFIG.PAGE_LIMIT,
			orderBy: CompanyLevelOrderBy.LEVEL_NAME_ASC,
		});
	const { data: employeeBands, isLoading: loadingEmployeeBands } =
		useGetEmployeeBand({
			companyId: currentCompany?.id,
			limit: APP_CONFIG.PAGE_LIMIT,
			orderBy: EmployeeBandOrderBy.NAME_ASC,
		});
	const { data: disabilities, isLoading: loadingDisabilities } =
		useGetDisabilities({
			organizationId: currentUser.user.organization?.id,
			limit: APP_CONFIG.PAGE_LIMIT,
			orderBy: DisabilitiesOrderBy.NAME_ASC,
		});

	useEffect(() => {
		const handleClickOutside = (event) => {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
				setIsOpen(false);
			}
		};

		if (isOpen) {
			document.addEventListener("mousedown", handleClickOutside);
		}

		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [isOpen]);

	const handleOpenExportModal = () => {
		setIsOpen(false);
		setOpenExportModal(true);
	};

	return (
		<div>
			<div className="border-b-[1.5px] pb-3 mb-4 flex w-full justify-between text-sm">
				<h2 className="font-semibold">Available Job Titles</h2>
				<div className="flex gap-2">
					<div className="flex items-center mx-1">
						<div className="relative" ref={dropdownRef}>
							{/* Dropdown Trigger Button */}
							<button
								onClick={() => setIsOpen(!isOpen)}
								className="border border-gray-300 rounded-lg text-[12px] text-gray-600 py-3 px-3 flex items-center hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
							>
								<Upload className="mr-3 text-[17px] text-gray-500" />
								Upload/Export
								<ChevronDown
									className={`ml-2 text-[14px] text-gray-500 transition-transform duration-200 ${
										isOpen ? "rotate-180" : ""
									}`}
								/>
							</button>

							{/* Dropdown Menu */}
							{isOpen && (
								<div className="absolute top-full left-0 mt-2 w-full min-w-[180px] bg-white border border-gray-200 rounded-xl shadow-lg z-50">
									<div className="p-2 space-y-2">
										<PermissionChecker
											permissionRule="OR"
											permissions={[PERMISSIONS.COMPANY_CONFIGS_WRITE]}
										>
											<button
												onClick={() => setOpenUploadJobTitlesModal(true)}
												className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 bg-gray-50 
									   border border-gray-200 rounded-lg transition hover:bg-gray-100 hover:shadow-sm"
											>
												<GoUpload className="text-[18px] text-gray-500" />
												Upload Job Titles
											</button>
										</PermissionChecker>
										<button
											onClick={handleOpenExportModal}
											className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 bg-gray-50 
									 border border-gray-200 rounded-lg transition hover:bg-gray-100 hover:shadow-sm"
										>
											<BsDownload className="text-[18px] text-gray-500" />
											Export Job Titles
										</button>
									</div>
								</div>
							)}
						</div>
					</div>
					{!showNewJobTitleForm && (
						<PermissionChecker
							permissions={[PERMISSIONS.COMPANY_CONFIGS_WRITE]}
						>
							<button
								onClick={() => setShowNewJobTitleForm(true)}
								className="border-none flex items-center text-primary-blue"
							>
								<AiOutlinePlus className="mr-2 text-lg" />
								Add New Job Title
							</button>
						</PermissionChecker>
					)}
				</div>
			</div>

			{showNewJobTitleForm && (
				<NewJobTitle
					companyLevels={companyLevels?.data || []}
					employeeBands={employeeBands?.data || []}
					disabilities={disabilities?.data || []}
					setShowNewJobTitleForm={setShowNewJobTitleForm}
				/>
			)}

			{isLoading &&
				!jobTitles.length &&
				checkUserPermissionsAnd([PERMISSIONS.COMPANY_CONFIGS_READ]) && (
					<div className="text-center mt-10 py-10">
						<SkewLoader color="#F58B00" />
					</div>
				)}

			{!loadingCompanyLevels &&
				!loadingEmployeeBands &&
				!loadingDisabilities && (
					<>
						{jobTitles.map((jobTitle) => (
							<EditJobTitle
								key={jobTitle.id}
								jobTitle={jobTitle}
								companyLevels={companyLevels?.data || []}
								employeeBands={employeeBands?.data || []}
								disabilities={disabilities?.data || []}
							/>
						))}
					</>
				)}

			{!isLoading &&
				!jobTitles.length &&
				checkUserPermissionsAnd([PERMISSIONS.COMPANY_CONFIGS_READ]) && (
					<div>
						<h3 className="text-center text-sm font-semibold mt-10">
							Sorry no job titles added yet. Click 'Add New Job Title' to begin.
						</h3>
					</div>
				)}

			{checkUserPermissionsAnd([PERMISSIONS.COMPANY_CONFIGS_READ]) ? (
				<div className="w-full flex justify-end mt-8">
					<CustomPagination pageCount={pageCount} />
				</div>
			) : (
				<div>
					<h3 className="text-center text-sm font-semibold mt-10">
						You are not authorized to access this data.
					</h3>
				</div>
			)}
			{openUploadJobTitlesModal && (
				<UploadJobTitles
					open={openUploadJobTitlesModal}
					setOpen={setOpenUploadJobTitlesModal}
				/>
			)}
			{openExportModal && currentCompany?.id && (
				<ExportJobTitlesModal
					open={openExportModal}
					setOpen={setOpenExportModal}
					companyId={currentCompany.id}
				/>
			)}
		</div>
	);
};

export default JobTitles;
